import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  sendChatMessage,
  uploadChatFile,
  askWithImage,
  imageProblem,
  chatErrorText,
  screenToOpen,
  actionsToShow,
  type ChatAction,
  type ChatResultItem,
  type ChatCard,
  type ChatBreakdown,
  type ChatHistoryItem,
} from '../lib/chatApi';
import { useDraggablePosition, type DragPosition } from '../hooks/useDraggablePosition';
import { useToast } from '../context/ToastContext';
import type { ChatContext, ChatOperation } from '../lib/chatApi';
import { Maximize2, Minimize2, X, Paperclip, Send, Bot } from 'lucide-react';
import { UserMessage, AssistantMessage } from './copilot/Messages';
import { LoadingIndicator, ErrorState } from './ui/States';
import { FOCUS } from './ui/tokens';

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  results?: ChatResultItem[];
  cards?: ChatCard[];
  breakdown?: ChatBreakdown[];
  /** What the user asked FOR. Decides what this message is allowed to draw. */
  responseType?: ChatOperation | null;
  /** A failed turn. Rendered as an error state, never as the assistant "saying" the error. */
  isError?: boolean;
  /**
   * The document sent with this turn: on the user message that attached it, and on the answer to
   * it, so both stay identifiable when scrolling back. Only the name is kept; the file is not stored.
   */
  attachmentName?: string;
  /** What was attached: a pasted image (read by the copilot) or a file (made into an RFQ). */
  attachmentKind?: 'image' | 'file';
  /** "Open <screen>" links the answer carries; always shown, whatever the answer's shape. */
  actions?: ChatAction[];
  ts: number;
};

const STORAGE_KEY = 'usc-copilot-chat-v1';
// v2: stores a viewport-relative fraction, not absolute pixels — so the widget stays in the
// same relative spot across browser zoom / display-scale changes instead of an old absolute
// coordinate landing somewhere different once the viewport's CSS-pixel size changes.
const POSITION_STORAGE_KEY = 'usc-copilot-position-v2';
/** Whether the user last left the panel expanded. A layout preference should outlive a session. */
const EXPANDED_STORAGE_KEY = 'usc-copilot-expanded-v1';
const MAX_HISTORY = 50;
const LAUNCHER_SIZE = 56; // px — matches the h-14 w-14 launcher; also the fixed clamp box for dragging
const LAUNCHER_MARGIN = 24; // px — matches the previous bottom-6/right-6 default
const PANEL_MARGIN = 12; // px kept clear from viewport edges when placing the open chat panel
const PANEL_GAP = 8; // px gap between the launcher and the panel when open
const LONG_PRESS_MS = 550; // touch-only hold duration that resets the launcher position

function getViewportSize() {
  const vv = window.visualViewport;
  return { width: vv?.width ?? window.innerWidth, height: vv?.height ?? window.innerHeight };
}

/** Below this the panel takes the whole screen — a floating box on a phone is unusable. */
const FULLSCREEN_BREAKPOINT = 640;

/**
 * Panel size.
 *
 * 420x600 is the size this panel has always been at rest, and it is what it goes back to: the
 * default was widened to 520x640 alongside the markdown work, and the bigger box was the wrong
 * trade — it sits over the page the whole time the chat is open, and most answers are a sentence
 * or two that never needed the extra room.
 *
 * The width argument for tables has not gone away, it just stops being the DEFAULT. A four-column
 * business table is still unreadable at 420px, which is what the expand control is for: the reader
 * asks for the room on the answer that needs it, and the choice is remembered. Three modes remain
 * — this default, expanded, and full screen on a phone.
 */
const PANEL_DEFAULT_WIDTH = 420;
const PANEL_DEFAULT_HEIGHT = 600;

/** The size the user dragged the panel to (D121.17), kept across sessions. */
const SIZE_STORAGE_KEY = 'usc-copilot-size-v1';
const PANEL_MIN_WIDTH = 320;
const PANEL_MIN_HEIGHT = 360;
const RESIZE_KEY_STEP = 20;

type PanelSize = { width: number; height: number };

function loadUserSize(): PanelSize | null {
  try {
    const raw = localStorage.getItem(SIZE_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && Number.isFinite(p.width) && Number.isFinite(p.height)) return { width: p.width, height: p.height };
    return null;
  } catch {
    return null;
  }
}

/** Minimum size up to the viewport less its margin (the viewport wins when it is smaller than the minimum). */
function clampPanelSize(size: PanelSize): PanelSize {
  const vp = getViewportSize();
  const maxW = vp.width - PANEL_MARGIN * 2;
  const maxH = vp.height - PANEL_MARGIN * 2;
  return {
    width: Math.round(Math.min(Math.max(size.width, Math.min(PANEL_MIN_WIDTH, maxW)), maxW)),
    height: Math.round(Math.min(Math.max(size.height, Math.min(PANEL_MIN_HEIGHT, maxH)), maxH)),
  };
}

/**
 * Which corner of the anchored panel is free (furthest from the launcher). computePanelPlacement
 * keeps the panel's left edge on a left-half launcher and its right edge on a right-half one; it
 * sits below a top-half launcher and above a bottom-half one.
 */
function freeCorner(launcher: DragPosition) {
  const vp = getViewportSize();
  return {
    horizontal: launcher.x + LAUNCHER_SIZE / 2 < vp.width / 2 ? 'right' : 'left',
    vertical: launcher.y + LAUNCHER_SIZE / 2 < vp.height / 2 ? 'bottom' : 'top',
  } as const;
}

/**
 * The centred panels (expanded, phone full screen) would cover the launcher. Shorten them
 * vertically so a PANEL_GAP band beside the launcher stays clear: the panel ends above a
 * launcher in the bottom half and starts below one in the top half. Stays inside the viewport.
 */
export function avoidLauncher(
  box: { left: number; top: number; width: number; height: number },
  launcher: { x: number; y: number },
  viewport: { width: number; height: number },
) {
  let top = Math.max(0, box.top);
  let bottom = Math.min(viewport.height, box.top + box.height);
  const bandTop = launcher.y - PANEL_GAP;
  const bandBottom = launcher.y + LAUNCHER_SIZE + PANEL_GAP;
  const touchesX = box.left < launcher.x + LAUNCHER_SIZE + PANEL_GAP && box.left + box.width > launcher.x - PANEL_GAP;
  if (touchesX && bottom > bandTop && top < bandBottom) {
    if (launcher.y + LAUNCHER_SIZE / 2 >= viewport.height / 2) bottom = Math.min(bottom, bandTop);
    else top = Math.max(top, bandBottom);
  }
  return { left: box.left, width: box.width, top, height: Math.max(0, bottom - top) };
}

function getPanelSize(expanded: boolean, userSize: PanelSize | null = null) {
  const { width, height } = getViewportSize();

  if (width < FULLSCREEN_BREAKPOINT) {
    return { width, height, fullscreen: true };
  }
  if (userSize && !expanded) {
    return { ...clampPanelSize(userSize), fullscreen: false };
  }
  if (expanded) {
    return {
      width: Math.min(Math.round(width * 0.66), width - PANEL_MARGIN * 2),
      height: Math.min(Math.round(height * 0.84), height - PANEL_MARGIN * 2),
      fullscreen: false,
    };
  }
  return {
    width: Math.min(PANEL_DEFAULT_WIDTH, width - PANEL_MARGIN * 2),
    height: Math.min(PANEL_DEFAULT_HEIGHT, height - PANEL_MARGIN * 2),
    fullscreen: false,
  };
}

/**
 * Anchors the chat panel to whichever side/corner of the launcher has room, then clamps the
 * result so the panel never renders outside the visible viewport.
 */
function computePanelPlacement(launcher: DragPosition, panelWidth: number, panelHeight: number) {
  const { width, height } = getViewportSize();
  const launcherCenterX = launcher.x + LAUNCHER_SIZE / 2;
  const launcherCenterY = launcher.y + LAUNCHER_SIZE / 2;

  let left = launcherCenterX < width / 2 ? launcher.x : launcher.x + LAUNCHER_SIZE - panelWidth;
  let top =
    launcherCenterY < height / 2
      ? launcher.y + LAUNCHER_SIZE + PANEL_GAP
      : launcher.y - panelHeight - PANEL_GAP;

  left = Math.min(Math.max(left, PANEL_MARGIN), Math.max(PANEL_MARGIN, width - panelWidth - PANEL_MARGIN));
  top = Math.min(Math.max(top, PANEL_MARGIN), Math.max(PANEL_MARGIN, height - panelHeight - PANEL_MARGIN));

  return { left, top };
}

const WELCOME: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  text: "Hi! I'm USC Copilot — your AI assistant for everything in the system.\n\nI can find records, give you stats, navigate to anywhere, and more.",
  ts: Date.now(),
};

const SUGGESTED_PROMPTS = [
  { label: '📊 Today summary',         text: 'Today summary' },
  { label: '⚠️ Overdue payables',     text: 'Show me overdue payables' },
  { label: '📥 Recent RFQs',           text: 'Recent RFQs' },
  { label: '🏷️ Low stock items',       text: 'Inventory low on stock' },
  { label: '💰 Receivables status',    text: 'Receivables summary' },
  { label: '🐟 Who supplies shrimp',   text: 'Who supplies shrimp' },
];

function loadFromStorage(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [WELCOME];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length) return parsed.slice(-MAX_HISTORY);
    return [WELCOME];
  } catch {
    return [WELCOME];
  }
}

function saveToStorage(messages: ChatMessage[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_HISTORY)));
  } catch {
    /* ignore quota errors */
  }
}

export const FloatingAssistant: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem(EXPANDED_STORAGE_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(EXPANDED_STORAGE_KEY, expanded ? '1' : '0'); } catch { /* quota */ }
  }, [expanded]);
  const [userSize, setUserSize] = useState<PanelSize | null>(loadUserSize);
  useEffect(() => {
    try {
      if (userSize) localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(userSize));
      else localStorage.removeItem(SIZE_STORAGE_KEY);
    } catch (err) {
      // The size still applies for this visit; only remembering it failed (storage full or blocked).
      console.warn('USC Copilot: could not remember the chat window size', err);
    }
  }, [userSize]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // A pasted image, with an object URL for its preview. Separate from `file`: a picked file is made
  // into an RFQ (/chat/upload, unchanged), a pasted image is a QUESTION for the copilot (D121.10).
  const [image, setImage] = useState<{ file: File; url: string } | null>(null);
  // Why a paste was refused, shown under the composer until the next paste or send.
  const [composerError, setComposerError] = useState<string | null>(null);
  // The preview URL is released when the image is replaced, removed or sent, and on unmount.
  useEffect(() => () => { if (image) URL.revokeObjectURL(image.url); }, [image]);
  const [messages, setMessages] = useState<ChatMessage[]>(loadFromStorage);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  // The launcher, so focus can be handed back when the panel closes.
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  // In flight, so a slow answer can be abandoned.
  const abortRef = useRef<AbortController | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();

  const { position, isDragging, isLongPressing, elementRef, onPointerDown, suppressNextClick, resetPosition } =
    useDraggablePosition({
      storageKey: POSITION_STORAGE_KEY,
      size: LAUNCHER_SIZE,
      margin: LAUNCHER_MARGIN,
      longPressMs: LONG_PRESS_MS,
      onPositionReset: () => toast.success('Chat position reset to default'),
    });

  const handleResetPosition = (e: React.MouseEvent) => {
    e.preventDefault(); // right-click: skip the native browser context menu
    resetPosition();
  };

  /**
   * An expanded or full-screen panel is CENTRED, not anchored to the launcher. Anchoring a
   * 66%-wide panel to a draggable button puts it half off-screen, and the launcher's position
   * stops being a useful hint once the panel is most of the viewport.
   */
  const measurePanel = React.useCallback(() => {
    const { width, height, fullscreen } = getPanelSize(expanded, userSize);
    const viewport = getViewportSize();
    if (fullscreen) return { ...avoidLauncher({ left: 0, top: 0, width, height }, position, viewport), fullscreen };
    if (expanded) {
      const centred = {
        left: Math.round((viewport.width - width) / 2),
        top: Math.round((viewport.height - height) / 2),
        width, height,
      };
      return { ...avoidLauncher(centred, position, viewport), fullscreen };
    }
    const { left, top } = computePanelPlacement(position, width, height);
    // Also run through avoidLauncher: a tall stored size clamped into the viewport can land on the
    // launcher. The panel then stops short of it (on the launcher's half); if even that is under the
    // minimum height, covering is avoided over honouring the minimum.
    return { ...avoidLauncher({ left, top, width, height }, position, viewport), fullscreen };
  }, [expanded, position, userSize]);

  const [panelStyle, setPanelStyle] = useState(measurePanel);

  // Resizing (D121.17). The first resize while expanded starts from the size on screen and turns
  // expanded off, so the panel does not jump to a different size under the pointer.
  const resizeFrom = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  const applySize = (next: PanelSize) => {
    setUserSize(clampPanelSize(next));
    setExpanded(false);
  };
  const outward = (dx: number, dy: number) => {
    const c = freeCorner(position);
    return { dw: c.horizontal === 'right' ? dx : -dx, dh: c.vertical === 'bottom' ? dy : -dy };
  };
  const onResizeDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const base = expanded ? getPanelSize(true) : panelStyle; // the preset, not its launcher-shortened form
    resizeFrom.current = { x: e.clientX, y: e.clientY, width: base.width, height: base.height };
  };
  const onResizeMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const s = resizeFrom.current;
    if (!s) return;
    const { dw, dh } = outward(e.clientX - s.x, e.clientY - s.y);
    applySize({ width: s.width + dw, height: s.height + dh });
  };
  const onResizeUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    resizeFrom.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };
  const onResizeKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const step: Record<string, [number, number]> = {
      ArrowLeft: [-RESIZE_KEY_STEP, 0], ArrowRight: [RESIZE_KEY_STEP, 0],
      ArrowUp: [0, -RESIZE_KEY_STEP], ArrowDown: [0, RESIZE_KEY_STEP],
    };
    const d = step[e.key];
    if (!d) return;
    e.preventDefault();
    const { dw, dh } = outward(d[0], d[1]);
    const base = expanded ? getPanelSize(true) : panelStyle;
    applySize({ width: base.width + dw, height: base.height + dh });
  };
  const resetSize = () => setUserSize(null);

  // Re-anchor the panel to the launcher whenever it opens, the launcher moves, or the
  // viewport changes size (browser resize, orientation change, mobile keyboard opening).
  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => setPanelStyle(measurePanel());
    reposition();
    window.addEventListener('resize', reposition);
    window.visualViewport?.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('resize', reposition);
      window.visualViewport?.removeEventListener('resize', reposition);
    };
  }, [open, position, expanded, measurePanel]);

  /**
   * Focus follows the panel. Opening a dialog and leaving focus behind it strands a keyboard user
   * on the page underneath; closing without handing focus back loses their place entirely.
   */
  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 60);
      return () => window.clearTimeout(id);
    }
    launcherRef.current?.focus();
    return undefined;
  }, [open]);

  // Persist on every message change
  useEffect(() => { saveToStorage(messages); }, [messages]);

  // Auto-scroll on new message
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading, open]);

  // Keyboard shortcut: Ctrl/Cmd + K to toggle
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const canSend = useMemo(() => !!input.trim() || !!file || !!image, [input, file, image]);

  /**
   * Ctrl+V of an image. Browsers put a copied image in clipboardData as a FILE and paste nothing
   * into a textarea, which is why pasting an image used to do nothing at all. Text in the same
   * clipboard still pastes as usual; only the image is taken from it.
   */
  const handlePaste = (e: React.ClipboardEvent) => {
    const item = Array.from(e.clipboardData?.items || []).find((i) => i.kind === 'file' && i.type.startsWith('image/'));
    const pasted = item?.getAsFile();
    if (!pasted) return;
    const hasText = Array.from(e.clipboardData.items).some((i) => i.kind === 'string' && i.type === 'text/plain');
    if (!hasText) e.preventDefault();
    const problem = imageProblem(pasted);
    if (problem) { setComposerError(problem); return; }
    setComposerError(null);
    // A clipboard image is usually called "image.png"; name it so the sent message says what it was.
    const named = new File([pasted], pasted.name && pasted.name !== 'image.png'
      ? pasted.name
      : `pasted-image-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${pasted.type.split('/')[1] || 'png'}`,
    { type: pasted.type });
    // One attachment at a time: a pasted image replaces a picked file, which would make an RFQ.
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
    setImage({ file: named, url: URL.createObjectURL(named) });
  };

  // The subject of the previous turn, echoed back so "which ones?" resolves without the user
  // retyping it. The server owns what goes in here; the client only carries it.
  const lastContext = useRef<ChatContext | null>(null);

  const buildHistoryPayload = (): ChatHistoryItem[] =>
    messages.slice(-8).map((m) => ({
      role: m.role,
      text: m.role === 'user' && m.attachmentName
        ? `${m.text}${m.text ? ' ' : ''}(attached: ${m.attachmentName})`
        : m.text,
    }));

  const runQuery = async (text: string, attachedFile: File | null = null, attachedImage: File | null = null) => {
    if (loading) return;
    const stamp = Date.now();
    const attached = attachedImage || attachedFile;
    const attachment = attached
      ? { attachmentName: attached.name, attachmentKind: (attachedImage ? 'image' : 'file') as 'image' | 'file' }
      : {};
    const userMessage: ChatMessage = {
      id: `user-${stamp}`,
      role: 'user',
      // The name travels as its own field, not folded into the text: it used to survive only when
      // the message had no words, so "summarise this" plus a PDF lost which PDF it was.
      text,
      ...attachment,
      ts: stamp,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    // The file now belongs to the sent message, so it leaves the pending bar the moment it is sent.
    // It used to be cleared only after the answer arrived, so during a slow upload (and for good if
    // the request failed) the same file showed in the sent bubble AND above the input.
    if (attachedFile) {
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    }
    if (attachedImage) setImage(null);
    setComposerError(null);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // A pasted image is a question (read by the copilot, same history and context as a typed one);
      // a picked file is still made into an RFQ, exactly as before.
      // The page on screen, so "this record" is the record the user is looking at (D121.13).
      const page = { path: `${location.pathname}${location.search}` };
      const response = attachedImage
        ? await askWithImage(text, attachedImage, buildHistoryPayload(), lastContext.current, controller.signal, page)
        : attachedFile
          ? await uploadChatFile(text, attachedFile)
          : await sendChatMessage(text, buildHistoryPayload(), lastContext.current, controller.signal, page);

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        text: response.reply || 'Done.',
        results: response.results || [],
        cards: response.cards || [],
        breakdown: response.breakdown || [],
        responseType: response.responseType ?? response.operation ?? null,
        ...attachment,
        actions: actionsToShow(response),
        ts: Date.now(),
      };
      lastContext.current = response.context ?? null;
      setMessages((prev) => [...prev, assistantMessage]);
      // "Take me to vendor invoices": open it. The panel stays open so the answer stays readable.
      const target = screenToOpen(response);
      if (target) navigate(target);
    } catch (err: any) {
      // A cancelled request is not a failure — the user asked for it to stop.
      if (controller.signal.aborted) return;
      // Put the file back so a retry does not mean picking it again, unless the user has already
      // attached a different one while this request was in flight.
      if (attachedFile) setFile((current) => current ?? attachedFile);
      if (attachedImage) {
        setImage((current) => current ?? { file: attachedImage, url: URL.createObjectURL(attachedImage) });
      }
      setMessages((prev) => [...prev, {
        id: `err-${Date.now()}`, role: 'assistant',
        // Worded from the error's code, never its message text; ErrorState says what to do next.
        text: chatErrorText(err),
        isError: true,
        ts: Date.now(),
      }]);
    } finally {
      abortRef.current = null;
      setLoading(false);
    }
  };

  /** Abandon a slow answer. Without this a hung model call left the panel stuck for good. */
  const handleCancel = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
  };

  const handleSend = () => {
    if (!canSend || loading) return;
    runQuery(input.trim(), image ? null : file, image ? image.file : null);
  };

  const handleQuickPrompt = (text: string) => {
    if (loading) return;
    runQuery(text, null);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleResultClick = (link?: string) => {
    if (!link) return;
    navigate(link);
    setOpen(false);
  };

  const handleClearChat = () => {
    if (!window.confirm('Clear conversation history?')) return;
    setMessages([WELCOME]);
    // What the last answer was about goes too: a follow-up after a clear names its own record.
    lastContext.current = null;
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            role="dialog"
            aria-label="USC Copilot chat"
            initial={{ opacity: 0, scale: 0.94, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: 'spring', stiffness: 340, damping: 28 }}
            aria-modal="false"
            className={[
              'fixed z-50 bg-white border border-slate-200 shadow-2xl flex flex-col overflow-hidden',
              panelStyle.fullscreen ? 'rounded-none' : 'rounded-2xl',
            ].join(' ')}
            style={{ left: panelStyle.left, top: panelStyle.top, width: panelStyle.width, height: panelStyle.height }}
          >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="relative h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Bot aria-hidden="true" size={18} className="relative" />
              </div>
              <div>
                <div className="text-[15px] font-bold leading-tight">USC Copilot</div>
                <div className="text-xs text-white/80 leading-tight">Ask about products, orders, suppliers</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleClearChat}
                className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                title="Clear conversation"
                aria-label="Clear conversation history"
              >
                Clear
              </button>
              {/* A four-column table cannot be read in a 420px panel. This is the control that
                  makes table-heavy answers legible, and the choice is remembered. */}
              {!panelStyle.fullscreen && (
                <button
                  onClick={() => setExpanded((e) => !e)}
                  className="h-7 w-7 grid place-items-center rounded bg-white/10 hover:bg-white/20 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  title={expanded ? 'Shrink panel' : 'Expand panel'}
                  aria-label={expanded ? 'Shrink chat panel' : 'Expand chat panel'}
                  aria-pressed={expanded}
                >
                  {expanded ? <Minimize2 aria-hidden="true" size={14} /> : <Maximize2 aria-hidden="true" size={14} />}
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="h-7 w-7 grid place-items-center rounded bg-white/10 hover:bg-white/20 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                title="Close (Esc)"
                aria-label="Close chat"
              >
                <X aria-hidden="true" size={15} />
              </button>
            </div>
          </div>

          {/* Messages */}
          {/* A live region, so a screen reader hears the answer instead of silently receiving it.
              "polite" because an answer should not interrupt what the user is already reading. */}
          <div
            ref={scrollRef}
            role="log"
            aria-live="polite"
            aria-relevant="additions text"
            aria-label="Conversation"
            className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 space-y-3 bg-slate-50"
          >
            {messages.map((msg) => (
              msg.role === 'user'
                ? <UserMessage key={msg.id} text={msg.text} ts={msg.ts} attachmentName={msg.attachmentName} attachmentKind={msg.attachmentKind} />
                : msg.isError
                  ? <ErrorState key={msg.id} message={msg.text} />
                  : (
                    <AssistantMessage
                      key={msg.id}
                      text={msg.text}
                      ts={msg.ts}
                      results={msg.results}
                      cards={msg.cards}
                      breakdown={msg.breakdown}
                      responseType={msg.responseType}
                      attachmentName={msg.attachmentName}
                      attachmentKind={msg.attachmentKind}
                      actions={msg.actions}
                      onResultClick={handleResultClick}
                    />
                  )
            ))}
            {loading && (
              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 inline-flex">
                <LoadingIndicator onCancel={handleCancel} />
              </div>
            )}
          </div>

          {/* Suggested prompts — only show when there's just the welcome message */}
          {messages.length <= 1 && (
            <div className="px-3 py-2 border-t border-slate-100 bg-white">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Try asking</div>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTED_PROMPTS.map((p) => (
                  <button
                    key={p.text}
                    onClick={() => handleQuickPrompt(p.text)}
                    className={`text-xs px-3 py-1.5 rounded-full bg-primary-50 text-primary-600 hover:bg-[#d4e5f1] border border-[#b8d4e8] transition-colors motion-reduce:transition-none ${FOCUS}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-slate-200 p-3 bg-white">
            {image && (
              <div className="mb-2 flex items-center gap-2 bg-primary-50 border border-[#b8d4e8] rounded-md p-1.5">
                <img
                  src={image.url}
                  alt="Pasted image to send"
                  className="h-14 w-14 shrink-0 rounded object-cover border border-[#b8d4e8] bg-white"
                />
                <span className="min-w-0 flex-1 text-xs text-primary-600 line-clamp-2 break-all" title={image.file.name}>
                  {image.file.name}
                </span>
                <button
                  onClick={() => setImage(null)}
                  className={`text-primary-600 hover:text-[#063e6b] shrink-0 rounded ${FOCUS}`}
                  aria-label={`Remove pasted image ${image.file.name}`}
                >
                  <X aria-hidden="true" size={14} />
                </button>
              </div>
            )}
            {composerError && (
              <p role="alert" className="mb-2 text-xs text-red-700">{composerError}</p>
            )}
            {file && (
              <div className="mb-2 flex items-center justify-between gap-2 bg-primary-50 border border-[#b8d4e8] rounded-md px-2 py-1.5 text-xs">
                <span className="text-primary-600 truncate min-w-0" title={file.name}>
                  <Paperclip aria-hidden="true" size={12} className="inline mr-1 -mt-0.5" />
                  {file.name}
                </span>
                <button
                  onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                  className={`text-primary-600 hover:text-[#063e6b] shrink-0 rounded ${FOCUS}`}
                  aria-label={`Remove attachment ${file.name}`}
                >
                  <X aria-hidden="true" size={14} />
                </button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <label
                className={`cursor-pointer h-10 w-10 shrink-0 grid place-items-center rounded-md border border-slate-200 hover:bg-slate-50 text-slate-500 focus-within:ring-2 focus-within:ring-primary-500`}
                title="Attach a file"
              >
                <Paperclip aria-hidden="true" size={16} />
                <span className="sr-only">Attach a file</span>
                <input
                  type="file"
                  ref={fileRef}
                  onChange={(e) => {
                    const picked = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    // One attachment at a time: a picked file replaces a pasted image.
                    if (picked) { setImage(null); setComposerError(null); }
                    setFile(picked);
                  }}
                  className="sr-only"
                />
              </label>
              {/* A textarea, not an input: the old control could not hold a newline at all, so
                  Shift+Enter did nothing and a two-part question had to be sent as two messages.
                  Its height is FIXED (h-10, the height of the buttons beside it). It used to grow
                  with what was typed, and sending cleared the text from code, which fires no
                  onChange, so the box kept the tall height of the last long message for good.
                  A long message now scrolls inside the box instead.

                  Two things keep a scrollbar out of the resting state. The placeholder is short
                  enough to sit on ONE line at this panel width — the long "Ask about products,
                  inventory, orders, POs, pricing..." wrapped to two, overflowed the one-line box
                  before a character was typed, and the browser drew a full arrowed scrollbar over
                  the composer. And `minimal-scrollbar` (index.css, the owner every other scroll
                  area here uses) means that when a long message overflows the box,
                  what appears is a 6px thumb on hover rather than a second chrome scrollbar. */}
              <textarea
                ref={inputRef}
                value={input}
                rows={1}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder="Ask me anything..."
                disabled={loading}
                aria-label="Ask USC Copilot"
                className={`flex-1 min-w-0 h-10 overflow-y-auto resize-none rounded-md border border-slate-200 px-3 py-2 text-[14px] leading-[1.5] outline-none focus:border-primary-500 disabled:opacity-50 minimal-scrollbar ${FOCUS}`}
              />
              <button
                onClick={handleSend}
                disabled={!canSend || loading}
                className={[
                  'h-10 px-4 shrink-0 rounded-md text-[13px] font-semibold transition-colors motion-reduce:transition-none',
                  'inline-flex items-center gap-1.5',
                  canSend && !loading
                    ? 'bg-primary-500 hover:bg-primary-600 text-white'
                    : 'bg-slate-200 text-slate-500 cursor-not-allowed',
                  FOCUS,
                ].join(' ')}
                title="Send (Enter)"
              >
                <Send aria-hidden="true" size={14} />
                Send
              </button>
            </div>
          </div>
          {!panelStyle.fullscreen && (() => {
            const c = freeCorner(position);
            return (
              <button
                type="button"
                aria-label="Resize chat panel"
                title="Drag to resize, arrow keys to adjust, double-click to reset"
                onPointerDown={onResizeDown}
                onPointerMove={onResizeMove}
                onPointerUp={onResizeUp}
                onPointerCancel={onResizeUp}
                onKeyDown={onResizeKey}
                onDoubleClick={resetSize}
                className={[
                  'absolute h-4 w-4 z-10 bg-transparent border-slate-400 hover:border-primary-600',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  c.vertical === 'top' ? 'top-1 border-t-2' : 'bottom-1 border-b-2',
                  c.horizontal === 'left' ? 'left-1 border-l-2' : 'right-1 border-r-2',
                  c.vertical === 'top' && c.horizontal === 'left' ? 'cursor-nwse-resize rounded-tl'
                    : c.vertical === 'bottom' && c.horizontal === 'right' ? 'cursor-nwse-resize rounded-br'
                    : 'cursor-nesw-resize',
                ].join(' ')}
                style={{ touchAction: 'none' }}
              />
            );
          })()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always mounted (D121.17): it is the toggle, and it paints above the panel (z-50). */}
        <div
          ref={elementRef}
          onPointerDown={onPointerDown}
          onContextMenu={handleResetPosition}
          className={`fixed z-[51] select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{
            left: position.x,
            top: position.y,
            width: LAUNCHER_SIZE,
            height: LAUNCHER_SIZE,
            touchAction: 'none',
          }}
        >
          <style>{`
            @keyframes usc-copilot-charge {
              from { transform: scale(0.6); opacity: 0.9; }
              to { transform: scale(1.35); opacity: 0; }
            }
          `}</style>
          {isLongPressing && (
            <span
              className="absolute inset-0 rounded-full border-2 border-white/80"
              style={{ animation: `usc-copilot-charge ${LONG_PRESS_MS}ms linear forwards` }}
              aria-hidden="true"
            />
          )}
          <button
            onClick={() => {
              if (suppressNextClick()) return;
              setOpen((o) => !o);
            }}
            aria-label={open ? 'Close USC Copilot chat' : 'Open USC Copilot chat assistant'}
            ref={launcherRef}
            aria-expanded={open}
            className={[
              'group relative h-14 w-14 rounded-full text-white shadow-2xl flex items-center justify-center',
              'bg-gradient-to-br from-primary-500 to-primary-600 hover:from-primary-600 hover:to-[#063e6b]',
              'transition-transform duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              isDragging ? 'scale-110 opacity-80 shadow-none' : 'hover:scale-110',
            ].join(' ')}
            title={`${open ? 'Close' : 'Open'} USC Copilot (⌘K) — drag to move, right-click or long-press to reset position`}
          >
            <span className="text-2xl">🤖</span>
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-amber-400 border-2 border-white rounded-full animate-pulse" aria-hidden="true" />
          </button>
        </div>
    </>
  );
};

